import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  validateCsvFile,
  validateCsvText,
} from './csvValidator';
import { buildFileSizeError } from './validationErrors';

const createEmptyValidationState = () => ({
  invalidRows: [],
  invalidRowCount: 0,
  fatalError: null,
  lineNumber: 0,
  rowCount: 0,
  fileHash: null,
  errorCount: 0,
  errorCategories: [],
});

const useBulkUploadValidation = ({
  selectedFile,
  requiredFields,
  maxSizeMb,
  maxSizeBytes,
}) => {
  const [validationState, setValidationState] = useState(createEmptyValidationState);
  const [isValidating, setIsValidating] = useState(false);
  const validationRunIdRef = useRef(0);
  const validationAbortRef = useRef(null);

  useEffect(() => () => {
    if (validationAbortRef.current) {
      validationAbortRef.current.abort();
      validationAbortRef.current = null;
    }
  }, []);

  const resetValidation = useCallback(() => {
    if (validationAbortRef.current) {
      validationAbortRef.current.abort();
      validationAbortRef.current = null;
    }
    validationRunIdRef.current += 1;
    setIsValidating(false);
    setValidationState(createEmptyValidationState());
  }, []);

  const validateSelectedFile = useCallback(async () => {
    if (!selectedFile) {
      return { isValid: false, rowCount: 0, fileHash: null };
    }
    if (selectedFile.size > maxSizeBytes) {
      const sizeError = buildFileSizeError(maxSizeMb);
      setValidationState({
        ...createEmptyValidationState(),
        fatalError: sizeError,
      });
      return { isValid: false, rowCount: 0, fileHash: null };
    }

    const runId = validationRunIdRef.current + 1;
    validationRunIdRef.current = runId;
    if (validationAbortRef.current) {
      validationAbortRef.current.abort();
    }
    const abortController = new AbortController();
    validationAbortRef.current = abortController;

    setIsValidating(true);
    setValidationState(createEmptyValidationState());

    const runValidation = async () => {
      if (typeof selectedFile.stream !== 'function') {
        let text = '';
        if (typeof selectedFile.text === 'function') {
          text = await selectedFile.text();
        } else {
          text = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result || '');
            reader.onerror = () => reject(reader.error);
            reader.readAsText(selectedFile);
          });
        }

        return validateCsvText({
          text,
          headers: requiredFields,
          onProgress: (progress) => {
            if (validationRunIdRef.current !== runId) {
              return;
            }
            setValidationState((previous) => ({
              ...previous,
              ...progress,
            }));
          },
        });
      }

      return validateCsvFile({
        file: selectedFile,
        headers: requiredFields,
        signal: abortController.signal,
        onProgress: (progress) => {
          if (validationRunIdRef.current !== runId) {
            return;
          }
          setValidationState((previous) => ({
            ...previous,
            ...progress,
          }));
        },
      });
    };

    try {
      const result = await runValidation();
      if (validationRunIdRef.current !== runId) {
        return { isValid: false, rowCount: 0, fileHash: null };
      }
      const nextState = {
        ...createEmptyValidationState(),
        ...result,
      };
      setValidationState(nextState);
      const isValidResult = !nextState.fatalError
        && nextState.invalidRowCount === 0
        && nextState.rowCount > 0;
      return {
        isValid: isValidResult,
        rowCount: nextState.rowCount,
        fileHash: nextState.fileHash || null,
      };
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.error('Validation failed:', error);
      }
      if (validationRunIdRef.current === runId && error?.name !== 'AbortError') {
        setValidationState({
          ...createEmptyValidationState(),
          fatalError: {
            type: 'VALIDATION_FAILED',
            message: 'Validation failed. Please try again.',
            lineNumber: 1,
          },
        });
      }
      return { isValid: false, rowCount: 0, fileHash: null };
    } finally {
      if (validationRunIdRef.current === runId) {
        setIsValidating(false);
      }
      if (validationAbortRef.current === abortController) {
        validationAbortRef.current = null;
      }
    }
  }, [maxSizeBytes, maxSizeMb, requiredFields, selectedFile]);

  return {
    validationState,
    isValidating,
    resetValidation,
    validateSelectedFile,
  };
};

export default useBulkUploadValidation;
